import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 450;
const CATALOG_CLOSE_DELAY = 500;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#scheduleClose(), {
      signal: this.#abortController.signal,
    });
    this.overflowMenu?.addEventListener('pointerenter', () => this.#cancelClose(), {
      signal: this.#abortController.signal,
    });
    this.addEventListener('click', this.#onClick, { signal: this.#abortController.signal });
    this.addEventListener('keydown', this.#onKeydown, { signal: this.#abortController.signal });

    onDocumentLoaded(this.#preloadImages);
    onDocumentLoaded(this.#evenCatalogTitles);
    document.fonts?.ready.then(() => this.#evenCatalogTitles());
    window.addEventListener('resize', this.#debouncedEvenCatalogTitles, { signal: this.#abortController.signal });
  }

  /**
   * Item closed with Escape. Focus returns to its top-level link, which must not reopen the panel.
   * @type {HTMLElement | null}
   */
  #dismissedItem = null;

  /** @type {Element | null} */
  #dismissedFocus = null;

  /**
   * A More row that has children stays open so the pointer can reach them.
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const link = event.target.closest('[slot="overflow"] > a.menu-list__link');
    if (!(link instanceof HTMLAnchorElement) || link.getAttribute('aria-haspopup') !== 'true') return;
    event.preventDefault();
  };

  /**
   * @param {KeyboardEvent} event
   */
  #onKeydown = (event) => {
    if (event.key !== 'Escape') return;
    const item = this.#state.activeItem;
    if (!item) return;

    event.preventDefault();
    this.#cancelClose();
    this.#debouncedActivateHandler.cancel();

    const fromOverflow = this.dataset.overflowExpanded === 'true';
    this.#collapseVisual(item);
    this.dataset.overflowExpanded = 'false';
    this.#state.activeItem = null;
    this.#dismissedItem = item;

    const target = fromOverflow ? this.querySelector('[slot="more"] .menu-list__link') : item;
    this.#dismissedFocus = target;
    if (target instanceof HTMLElement) target.focus();
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the More overflow panel itself is hovered.
   * Do not use the whole overflow-list: the open dropdown sits over the product title,
   * so treating the list as hovered keeps Franchise / More from fully closing.
   * @returns {boolean}
   */
  get overflowHovered() {
    if (this.overflowMenu?.matches(':hover')) return true;
    // More's white card is [part="overflow-list"]; keep the panel open while the
    // pointer crosses gaps between overflow rows (e.g. toward Authorization Query).
    const list = this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]');
    return list instanceof HTMLElement ? list.matches(':hover') : false;
  }

  /**
   * Keep the same gap between the four series titles. A long entry makes its
   * column wider than the title, so the title gap is the larger of a roomy
   * minimum and whatever that entry needs to clear the next title.
   */
  #evenCatalogTitles = () => {
    const list = this.querySelector('.mega-menu__grid--catalog .mega-menu__list');
    if (!list) return;
    const cols = [...list.querySelectorAll(':scope > .mega-menu__column')].filter((col) =>
      col.querySelector('.mega-menu__link--parent')
    );
    if (cols.length < 2) return;

    const measured = cols.map((col) => {
      const widths = [...col.querySelectorAll('.mega-menu__link-title')].map((el) => el.getBoundingClientRect().width);
      const title = col.querySelector('.mega-menu__link--parent .mega-menu__link-title');
      return { col, titleW: title.getBoundingClientRect().width, maxItem: Math.max(0, ...widths) };
    });
    if (measured.some((item) => item.titleW < 1)) return;

    const columnGap = parseFloat(getComputedStyle(list).columnGap) || 0;
    const minGap = 80;
    const gap = measured.slice(0, -1).reduce((widest, item) => {
      return Math.max(widest, item.maxItem - item.titleW + columnGap);
    }, minGap);

    measured.forEach((item, index) => {
      const last = index === measured.length - 1;
      const width = last
        ? Math.max(item.titleW, item.maxItem)
        : Math.max(item.titleW + (gap - columnGap), item.maxItem);
      item.col.style.width = `${Math.ceil(width)}px`;
    });
  };

  #debouncedEvenCatalogTitles = debounce(this.#evenCatalogTitles, 150);

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#cancelClose();
    this.#debouncedActivateHandler.cancel();
    this.#evenCatalogTitles();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#cancelClose();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');
    const reopening = this.hasAttribute('data-submenu-closing');
    this.removeAttribute('data-submenu-closing');

    if (!(event.target instanceof Element)) return;

    // Open a column only when the pointer is on the label, not the padded gap beside it.
    if (event.type === 'pointerenter' && !event.target.closest('.menu-list__link-title, .menu-list__link')) {
      return;
    }

    let item = findMenuItem(event.target);

    if (item && item === this.#dismissedItem && event.type !== 'pointerenter') return;
    this.#dismissedItem = null;

    if (!item || (item == this.#state.activeItem && !reopening)) return;

    const slotName = event.target.closest('[slot]')?.getAttribute('slot') || '';
    const isMore = slotName === 'more' || slotName === 'overflow';

    this.dataset.overflowExpanded = String(isMore);

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';
    this.#setPanelPointerEvents('');

    let submenu = findSubmenu(item);
    let overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;

    if (!submenu && isMore) {
      submenu = this.overflowMenu;
    }

    const isCard = Boolean(submenu?.querySelector('.mega-menu__grid--card, .mega-menu__grid--partner'));
    const submenuHeight = isMore
      ? (this.overflowMenu?.offsetHeight ?? 0)
      : submenu
        ? isCard
          ? submenu.offsetHeight
          : Math.max(submenu.offsetHeight, overflowMenuHeight)
        : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');

    if (isMore) {
      alignMoreCard(this);
      this.#syncMorePanelHeight();
    } else if (submenu && !item.closest('[slot]')?.getAttribute('slot')) {
      alignListUnderTitle(item, submenu);
    }
  };

  /**
   * After Partnerships (etc.) expands inside More, remeasure so --submenu-height
   * and the black backdrop stay at least as tall as the white card.
   */
  #syncMorePanelHeight = () => {
    requestAnimationFrame(() => {
      if (this.dataset.overflowExpanded !== 'true') return;
      const list = this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]');
      const panel = this.overflowMenu;
      const listHeight = list instanceof HTMLElement ? list.offsetHeight : 0;
      const panelHeight = panel?.offsetHeight ?? 0;
      const height = Math.max(listHeight, panelHeight, 0);
      if (height > 0) this.style.setProperty('--submenu-height', `${height}px`);
    });
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay.
   * Do not hide immediately: the pointer needs time to reach submenu links.
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (this.#dismissedItem && event.type !== 'pointerleave') {
      const next = 'relatedTarget' in event ? event.relatedTarget : null;
      const stays =
        next instanceof Node &&
        (this.#dismissedItem.parentElement?.contains(next) || next === this.#dismissedFocus);
      if (!stays) this.#dismissedItem = null;
    }

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      const next = 'relatedTarget' in event ? event.relatedTarget : null;
      if (next instanceof Node && (item.parentElement?.contains(next) || this.overflowMenu?.contains(next))) {
        return;
      }
      if (next instanceof Element && next.closest('[slot="overflow"], [slot="more"]')) return;
      if (item.closest('[slot="overflow"], [slot="more"]') && this.#pointerStillInside()) return;
      this.#scheduleClose();
    } else if (item?.closest('[slot="overflow"], [slot="more"]') && this.#pointerStillInside()) {
      this.#cancelClose();
    }
  }

  #cancelClose() {
    this.#closeTimer && clearTimeout(this.#closeTimer);
    this.#closeTimer = 0;
  }

  #scheduleClose() {
    this.#cancelClose();
    const delay = this.#closeDelay();
    this.#closeTimer = window.setTimeout(() => {
      this.#closeTimer = 0;
      this.#deactivate();
    }, delay);
  }

  /**
   * Catalog is a taller mega menu, so it stays open a little longer.
   * More / Franchise still wait long enough to click a link.
   * @returns {number}
   */
  #closeDelay() {
    const item = this.#state.activeItem;
    const submenu = item ? findSubmenu(item) : null;
    if (submenu?.querySelector('.mega-menu__grid--catalog')) return CATALOG_CLOSE_DELAY;
    return DEACTIVATE_DELAY;
  }

  /** @type {number} */
  #closeTimer = 0;

  /**
   * Hide open panels now so they cannot stay painted over the product title.
   * @param {HTMLElement | null} [item]
   */
  #collapseVisual(item = this.#state.activeItem) {
    this.dataset.submenuClosing = 'true';
    this.#setPanelPointerEvents('none');
    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.ariaExpanded = 'false';
    if (item) item.ariaExpanded = 'false';
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.#pointerStillInside()) return;

    this.#collapseVisual(item);
    this.dataset.overflowExpanded = 'false';
    this.#state.activeItem = null;
    item.removeAttribute('data-animating');
  };

  /**
   * @returns {boolean}
   */
  #pointerStillInside() {
    const item = this.#state.activeItem;
    const submenu = item ? findSubmenu(item) : null;
    const hoveredOverflow = [...this.querySelectorAll('[slot="overflow"] a, [slot="more"] button, [slot="more"] a')].some(
      (el) => el.matches(':hover')
    );
    // Side flyout for Partnerships (etc.) sits outside the white card; keep More open on it.
    const hoveredOverflowSubmenu = [...this.querySelectorAll('[slot="overflow"] .menu-list__submenu')].some((el) =>
      el.matches(':hover')
    );
    return (
      this.overflowHovered ||
      hoveredOverflow ||
      hoveredOverflowSubmenu ||
      (submenu?.matches(':hover') ?? false) ||
      (item?.parentElement?.matches(':hover') ?? false)
    );
  }

  /**
   * @param {string} value
   */
  #setPanelPointerEvents(value) {
    const panels = this.querySelectorAll('.menu-list__submenu');
    panels.forEach((panel) => {
      if (panel instanceof HTMLElement) panel.style.pointerEvents = value;
    });
    if (this.overflowMenu) this.overflowMenu.style.pointerEvents = value;
  }

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  const more = element.closest('[slot="more"]');
  if (more) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(more.parentElement?.querySelector('[slot="overflow"]'));
  }

  const item = element.closest('.menu-list__list-item') ?? element;
  const menuitem = item.querySelector('[ref="menuitem"]');
  return menuitem instanceof HTMLElement ? menuitem : null;
}

/**
 * Shift a list-style dropdown so it starts under its own top-level title,
 * without letting it run past the panel's right edge.
 * @param {HTMLElement} item
 * @param {HTMLElement} submenu
 */
/**
 * Place the More card under the More label.
 * @param {HeaderMenu} menu
 */
function alignMoreCard(menu) {
  const more = menu.querySelector('[slot="more"] .menu-list__link-title');
  const list = menu.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]');
  if (!(more instanceof HTMLElement) || !(list instanceof HTMLElement)) return;

  menu.style.setProperty('--more-card-left', '0px');
  const desired = more.getBoundingClientRect().left - list.getBoundingClientRect().left;
  const maxShift = Math.max(0, window.innerWidth - 16 - 320 - list.getBoundingClientRect().left);
  menu.style.setProperty('--more-card-left', `${Math.round(Math.min(Math.max(0, desired), maxShift))}px`);
}

function alignListUnderTitle(item, submenu) {
  if (submenu.querySelector('.mega-menu__grid--card, .mega-menu__grid--partner') && !item.closest('[slot="overflow"]')) {
    const title = item.querySelector('.menu-list__link-title') || item;
    const parent = submenu.offsetParent;
    if (!(parent instanceof HTMLElement)) return;
    const desired = title.getBoundingClientRect().left - parent.getBoundingClientRect().left;
    const cardWidth = 320;
    const maxLeft = Math.max(8, parent.clientWidth - cardWidth - 16);
    submenu.style.setProperty('--menu-card-left', `${Math.round(Math.min(Math.max(8, desired), maxLeft))}px`);
    return;
  }

  const grid = submenu.querySelector('.mega-menu__grid--list');
  const list = grid?.querySelector('.mega-menu__list');
  if (!(grid instanceof HTMLElement) || !(list instanceof HTMLElement)) return;

  const title = item.querySelector('.menu-list__link-title') || item;
  grid.style.setProperty('--mega-menu-list-offset', '0px');
  void list.offsetWidth;

  const desired = title.getBoundingClientRect().left;
  const start = list.getBoundingClientRect().left;
  const maxShift = Math.max(0, grid.getBoundingClientRect().right - list.offsetWidth - start);
  const shift = Math.max(0, Math.min(desired - start, maxShift));
  grid.style.setProperty('--mega-menu-list-offset', `${Math.round(shift)}px`);
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
